import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import { coDeSysData, bacnetData } from '../../data/m10_u1_x1_data';
import { useAuth } from '../../context/AuthContext';
import { useUnitOverride, updateUnitContent } from '../../services/unitOverridesService';

const PAGE_ID = 'm10_u1_x1_';

const formatRichText = (
  text: string,
  colors: { codeBg: string; codeText: string; strongColor: string }
): React.ReactNode => {
  if (!text) return '';
  
  // Split by newlines to preserve paragraph structure
  const paragraphs = text.split('\n');
  
  return paragraphs.map((paragraph, idx) => {
    if (!paragraph.trim()) {
      return <br key={idx} />;
    }

    // Check if it's a list item
    if (paragraph.trim().startsWith('- ')) {
      return (
        <Box
          component="div"
          key={idx}
          sx={{ pl: 2, my: 0.5, display: 'flex' }}
        >
          <span style={{ marginRight: '8px' }}>•</span>
          <span>{paragraph.trim().substring(2)}</span>
        </Box>
      );
    }

    // Regular paragraph with optional bold/code formatting
    const formatted = paragraph
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

    return (
      <Typography
        key={idx}
        variant="body2"
        sx={{
          mb: 1,
          lineHeight: 1.6,
          '& code': {
            backgroundColor: colors.codeBg,
            padding: '2px 6px',
            borderRadius: '4px',
            fontFamily: '"Courier New", monospace',
            fontSize: '0.9em',
            color: colors.codeText
          },
          '& strong': {
            fontWeight: 600,
            color: colors.strongColor
          }
        }}
        dangerouslySetInnerHTML={{ __html: formatted }}
      />
    );
  });
};

const renderList = (items: string[]) => (
  <Box component="ul" sx={{ pl: 3, m: 0 }}>
    {items.map((item) => (
      <Box component="li" key={item} sx={{ mb: 0.5, lineHeight: 1.6 }}>
        {item}
      </Box>
    ))}
  </Box>
);

const M10_U1_X1: React.FC = () => {
  const { totalScore, token, canViewM10Datentypen, canEditM10DatentypenAccess } = useAuth();
  const { override, refresh } = useUnitOverride(PAGE_ID);
  const { darkMode } = useTheme();
  const textPrimary = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;
  const textSecondary = darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary;
  const headingColor = darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1;
  const accentColor = tokens.colors.primary;
  const surfaceAlt = darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt;
  const borderColor = darkMode ? tokens.colors.dark.border : tokens.colors.light.border;
  const shadow = darkMode ? tokens.colors.dark.shadow : tokens.colors.light.shadow;
  const richTextColors = {
    codeBg: surfaceAlt,
    codeText: textPrimary,
    strongColor: headingColor
  };

  const currentRequired = Number(override?.design?.requiredLevel ?? 1000);

  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedCoDeSysIndex, setSelectedCoDeSysIndex] = React.useState(0);
  const [selectedBacnetIndex, setSelectedBacnetIndex] = React.useState(0);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [requiredLevel, setRequiredLevel] = React.useState<string>(String(currentRequired));
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const currentCoDeSys = coDeSysData[selectedCoDeSysIndex] ?? coDeSysData[0];
  const currentBacnet = bacnetData[selectedBacnetIndex] ?? bacnetData[0];

  // Debug: Überprüfe die Bildpfade
  React.useEffect(() => {
    console.log('Current CoDeSys image:', currentCoDeSys.image);
    console.log('Current BACnet image:', currentBacnet.image);
  }, [currentCoDeSys, currentBacnet]);

  // Zugriffskontrolle via Override-RequiredLevel (Default 1000)
  if (!canViewM10Datentypen && totalScore < currentRequired) {
    return (
      <UnitPageShell title="Datentypen & BACnet Objekte">
        <Box sx={{ p: 4 }}>
          <Alert severity="warning">
            Für dieses Modul benötigst du mindestens {currentRequired} Punkte. Deine aktuellen Punkte: {totalScore}
          </Alert>
          <Navigate to="/modules" replace />
        </Box>
      </UnitPageShell>
    );
  }

  const handleOpen = () => {
    setRequiredLevel(String(Number(override?.design?.requiredLevel ?? 1000)));
    setError(null);
    setSuccess(null);
    setDialogOpen(true);
  };

  const handleClose = () => setDialogOpen(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const val = parseInt(requiredLevel, 10);
      if (isNaN(val) || val < 0) {
        setError('Bitte eine gültige, nicht-negative Zahl eingeben.');
        setSubmitting(false);
        return;
      }
      const design = { ...(override?.design || {}), requiredLevel: val };
      await updateUnitContent(PAGE_ID, { design }, token!);
      await refresh();
      setSuccess('Mindestpunktzahl erfolgreich gespeichert.');
      setDialogOpen(false);
    } catch (e: any) {
      setError(e?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const tabsElement = React.createElement(
    Tabs,
    {
      value: activeTab,
      onChange: (_event, value) => setActiveTab(value),
      variant: 'fullWidth',
      sx: {
        borderBottom: 1,
        borderColor: 'divider',
        mb: 3,
        '& .MuiTab-root': {
          fontWeight: 600,
          color: textSecondary
        },
        '& .Mui-selected': {
          color: `${accentColor} !important`
        }
      }
    },
    React.createElement(Tab, { label: 'CoDeSys Datentypen' }),
    React.createElement(Tab, { label: 'BACnet Objekte' })
  );

  return React.createElement(
    UnitPageShell,
    { title: '10.1 Datentypen & BACnet Objekte' },
    React.createElement(
      Box,
      { sx: { width: '100%' } },
      React.createElement(
        Box,
        { sx: { display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' } },
        React.createElement(
          Typography,
          { variant: 'body2', sx: { fontWeight: 500 } },
          `Aktuelle Mindestpunktzahl: ${currentRequired} · Dein Punktestand: ${totalScore}`
        ),
        canEditM10DatentypenAccess
          ? React.createElement(
              Button,
              { variant: 'contained', color: 'primary', onClick: handleOpen },
              'Mindestpunktzahl festlegen'
            )
          : React.createElement(
              Alert,
              { severity: 'info', sx: { m: 0, px: 1.5, py: 0.5 } },
              'Nur berechtigte Nutzer können die Mindestpunktzahl ändern.'
            )
      ),
      tabsElement,
      activeTab === 0
          ? React.createElement(
              Box,
              null,
              React.createElement(
                Typography,
                { variant: 'subtitle1', sx: { fontWeight: 600, color: accentColor, mb: 1 } },
                'CoDeSys Datentyp auswählen:'
              ),
              React.createElement(
                FormControl,
                { fullWidth: true, sx: { mb: 3 } },
                React.createElement(InputLabel, { id: 'codesys-select-label' }, 'CoDeSys Datentyp'),
                React.createElement(
                  Select,
                  {
                    labelId: 'codesys-select-label',
                    value: selectedCoDeSysIndex,
                    label: 'CoDeSys Datentyp',
                    onChange: (event) => setSelectedCoDeSysIndex(Number(event.target.value)),
                    MenuProps: { PaperProps: { sx: { maxHeight: 420 } } }
                  },
                  coDeSysData.map((entry, index) =>
                    React.createElement(MenuItem, { key: entry.name, value: index }, entry.name)
                  )
                )
              ),
              React.createElement(
                Box,
                { sx: { display: 'flex', justifyContent: 'center', mb: 3 } },
                React.createElement('img', {
                  src: currentCoDeSys.image,
                  alt: currentCoDeSys.name,
                  onError: (e: any) => console.error('Image failed to load:', currentCoDeSys.image, e),
                  onLoad: () => console.log('Image loaded:', currentCoDeSys.image),
                  style: {
                    maxWidth: 320,
                    width: 'min(50vw, 320px)',
                    height: 'auto',
                    borderRadius: 12,
                    boxShadow: shadow
                  }
                })
              ),
              React.createElement(
                Typography,
                { variant: 'h4', sx: { fontWeight: 700, color: headingColor, mb: 2 } },
                currentCoDeSys.name
              ),
              React.createElement(Divider, { sx: { mb: 3 } }),
              React.createElement(
                Box,
                { sx: { display: 'grid', gap: 2.5 } },
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Beschreibung'
                  ),
                  React.createElement(
                    Box,
                    { sx: { mb: 2 } },
                    formatRichText(currentCoDeSys.description, richTextColors)
                  )
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Größe'
                  ),
                  React.createElement(
                    Typography,
                    { variant: 'body1', sx: { lineHeight: 1.7, color: textPrimary } },
                    currentCoDeSys.sizeInBits
                  )
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Wertebereich'
                  ),
                  React.createElement(
                    Typography,
                    { variant: 'body1', sx: { lineHeight: 1.7, color: textPrimary } },
                    currentCoDeSys.range
                  )
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Bit-Belegung'
                  ),
                  React.createElement(
                    Box,
                    { sx: { mb: 2 } },
                    formatRichText(currentCoDeSys.bitLayoutExplanation, richTextColors)
                  )
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Typische Verwendung'
                  ),
                  React.createElement(
                    Box,
                    { sx: { mb: 2 } },
                    formatRichText(currentCoDeSys.usageExamples, richTextColors)
                  )
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Syntax (Deklaration)'
                  ),
                  React.createElement(
                    Box as any,
                    {
                      component: 'pre',
                      sx: {
                        backgroundColor: surfaceAlt,
                        borderRadius: 2,
                        p: 2,
                        fontFamily: '"Fira Code", "Courier New", monospace',
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: textPrimary,
                        overflow: 'auto',
                        border: `1px solid ${borderColor}`,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }
                    },
                    currentCoDeSys.syntaxExample
                  )
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Initialisierung'
                  ),
                  React.createElement(
                    Box as any,
                    {
                      component: 'pre',
                      sx: {
                        backgroundColor: surfaceAlt,
                        borderRadius: 2,
                        p: 2,
                        fontFamily: '"Fira Code", "Courier New", monospace',
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: textPrimary,
                        overflow: 'auto',
                        border: `1px solid ${borderColor}`,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }
                    },
                    currentCoDeSys.initializationExample
                  )
                )
              )
            )
          : React.createElement(
              Box,
              null,
              React.createElement(
                Typography,
                { variant: 'subtitle1', sx: { fontWeight: 600, color: accentColor, mb: 1 } },
                'BACnet Objekt auswählen:'
              ),
              React.createElement(
                FormControl,
                { fullWidth: true, sx: { mb: 3 } },
                React.createElement(InputLabel, { id: 'bacnet-select-label' }, 'BACnet Objekt'),
                React.createElement(
                  Select,
                  {
                    labelId: 'bacnet-select-label',
                    value: selectedBacnetIndex,
                    label: 'BACnet Objekt',
                    onChange: (event) => setSelectedBacnetIndex(Number(event.target.value)),
                    MenuProps: { PaperProps: { sx: { maxHeight: 420 } } }
                  },
                  bacnetData.map((entry, index) =>
                    React.createElement(MenuItem, { key: entry.name, value: index }, entry.name)
                  )
                )
              ),
              React.createElement(
                Box,
                { sx: { display: 'flex', justifyContent: 'center', mb: 3 } },
                React.createElement('img', {
                  src: currentBacnet.image,
                  alt: currentBacnet.name,
                  onError: (e: any) => console.error('BACnet image failed to load:', currentBacnet.image, e),
                  onLoad: () => console.log('BACnet image loaded:', currentBacnet.image),
                  style: {
                    maxWidth: 320,
                    width: 'min(50vw, 320px)',
                    height: 'auto',
                    borderRadius: 12,
                    boxShadow: shadow
                  }
                })
              ),
              React.createElement(
                Typography,
                { variant: 'h4', sx: { fontWeight: 700, color: headingColor, mb: 2 } },
                `${currentBacnet.name} (${currentBacnet.objectTypeIdentifier})`
              ),
              React.createElement(Divider, { sx: { mb: 3 } }),
              React.createElement(
                Box,
                { sx: { display: 'grid', gap: 2.5 } },
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Beschreibung'
                  ),
                  React.createElement(
                    Box,
                    { sx: { mb: 2 } },
                    formatRichText(currentBacnet.description, richTextColors)
                  )
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Wichtige Properties'
                  ),
                  renderList(currentBacnet.commonProperties)
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Typische Services'
                  ),
                  renderList(currentBacnet.typicalServices)
                ),
                React.createElement(
                  Box,
                  null,
                  React.createElement(
                    Typography,
                    { variant: 'subtitle2', sx: { fontWeight: 600, color: headingColor, mb: 0.5 } },
                    'Details'
                  ),
                  React.createElement(
                    Box,
                    { sx: { mb: 2 } },
                    formatRichText(currentBacnet.detailedExplanation, richTextColors)
                  )
                )
              )
          )
    )
    ,
    React.createElement(AccessDialog, {
      open: dialogOpen,
      onClose: handleClose,
      onConfirm: handleConfirm,
      submitting,
      error,
      success,
      requiredLevel,
      setRequiredLevel
    })
  );
};

// Dialog für Admin: Mindestpunktzahl setzen
// Er wird gerendert als Teil des Haupt-Return, aber separat für bessere Lesbarkeit.

const AccessDialog: React.FC<{ open: boolean; onClose: () => void; onConfirm: () => Promise<void>; submitting: boolean; error: string | null; success: string | null; requiredLevel: string; setRequiredLevel: (v: string) => void; }> = ({ open, onClose, onConfirm, submitting, error, success, requiredLevel, setRequiredLevel }) => (
  React.createElement(
    Dialog,
    { open, onClose, fullWidth: true, maxWidth: 'sm' },
    React.createElement(DialogTitle, null, 'Einstellungen: Zugangsvoraussetzung'),
    React.createElement(
      DialogContent,
      null,
      React.createElement(
        Typography,
        { variant: 'body2', sx: { mb: 2 } },
        'Beschreibung und Auswirkungen: - Die Mindestpunktzahl steuert den Zugriff auf diese Seite. - Nutzer ohne ausreichende Punkte sehen die Seite ggf. nicht im Menü bzw. erhalten keinen Zugriff. - Administratoren sind davon nicht betroffen.'
      ),
      React.createElement(TextField, {
        label: 'Mindestpunktzahl',
        type: 'number',
        fullWidth: true,
        value: requiredLevel,
        onChange: e => setRequiredLevel(e.target.value),
        inputProps: { min: 0 }
      }),
      error ? React.createElement(Alert, { sx: { mt: 2 }, severity: 'error' }, error) : null,
      success ? React.createElement(Alert, { sx: { mt: 2 }, severity: 'success' }, success) : null
    ),
    React.createElement(
      DialogActions,
      null,
      React.createElement(Button, { onClick: onClose, disabled: submitting }, 'Abbrechen'),
      React.createElement(Button, { onClick: onConfirm, variant: 'contained', disabled: submitting }, 'Bestätigen')
    )
  )
);

export default M10_U1_X1;